import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { actionUpdateIsEmailVerified } from 'src/app/state/modules/session/isEmailVerified';
import { actionUpdateEmailAddressVerified as actionUpdateIsEmailVerifiedTealium } from 'src/app/state/modules/tealium/emailAddressVerified';
import { isDev } from 'src/app/utils/environment';
import { getIsAdvertiserEmailUnverified } from 'src/app/utils/getIsAdvertiserEmailUnverified';
import { log } from 'src/app/utils/logging';
import { useEmailVerificationSync } from 'src/app/views/hooks/useEmailVerificationSync';
import { useIdentityLazyQuery } from 'src/graphql/generated';
import type AppState from 'src/types/appState';

export const useHandleEmailVerification = () => {
  const {
    advertiserId,
    isEmailVerifiedInSSO,
    isEmailUnverifiedInAdcentre,
    seekAdvertiserId,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.advertiserId,
      isEmailVerifiedInSSO: state.session.isEmailVerified,
      isEmailUnverifiedInAdcentre: getIsAdvertiserEmailUnverified(
        state.session.hirerAccount.status,
      ),
      seekAdvertiserId: state.session.seekAdvertiserId,
    }),
    shallowEqual,
  );
  const { emailVerificationSync } = useEmailVerificationSync();
  const dispatch = useDispatch();

  const [getIdentityLazyQuery] = useIdentityLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const emailVerified = Boolean(data?.identity.subject.emailVerified);
      dispatch(actionUpdateIsEmailVerified(emailVerified));
      dispatch(actionUpdateIsEmailVerifiedTealium(emailVerified));
    },
    onError: (error) => {
      log.error('useHandleEmailVerification error', { error });
    },
    ...(isDev() && {
      context: {
        headers: { advertiserId },
      },
    }),
  });

  const handleEmailVerification = async (): Promise<boolean> => {
    if (!isEmailVerifiedInSSO) {
      await getIdentityLazyQuery();

      return false;
    }

    if (
      isEmailVerifiedInSSO &&
      isEmailUnverifiedInAdcentre &&
      seekAdvertiserId
    ) {
      emailVerificationSync();
    }

    return true;
  };

  return {
    handleEmailVerification,
  };
};
